import { getCoverThumbUrl } from '../../utils/coverUrl';
import React from 'react';
import { Video, Play } from 'lucide-react';

/**
 * FeaturedTrailersPanel — admin selects up to 5 trailers (videos linked
 * to existing books) that appear on the public landing page. Pure
 * presentation: parent owns `booksWithTrailers`, the slot→book mapping,
 * and all mutation handlers.
 *
 * Extracted from AdminSettings.jsx (2026-05-29) to bring that page
 * below the 1000-line health threshold.
 */
const FeaturedTrailersPanel = ({
  booksWithTrailers,
  featuredTrailers,
  savingTrailer,
  onSetTrailer,
  onRemoveTrailer,
  getCoverUrl,
}) => {
  return (
    <div className="bg-white/5 rounded-xl border border-white/10 p-6" data-testid="featured-trailer-section">
      <div className="flex items-center mb-6">
        <Video className="h-6 w-6 text-red-500 mr-2" />
        <h2 className="text-xl font-bold text-white">Featured Trailers (5 Slots)</h2>
      </div>

      {booksWithTrailers.length === 0 ? (
        <div className="text-center py-8 bg-white/5 rounded-lg border border-dashed border-white/20">
          <Video className="h-12 w-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400 mb-2">No book trailers available yet</p>
          <p className="text-gray-500 text-sm">
            Upload a trailer or add a YouTube/Facebook link when editing a book to enable this feature.
          </p>
        </div>
      ) : (
        <>
          <p className="text-gray-400 text-sm mb-4">
            Select up to 5 trailers to display on the landing page. Supports uploaded videos, YouTube, and Facebook links.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {['1', '2', '3', '4', '5'].map((position) => (
              <div
                key={position}
                className={`relative rounded-lg border-2 p-4 transition-all ${
                  featuredTrailers[position]
                    ? 'border-red-500/50 bg-red-500/10'
                    : 'border-dashed border-white/20 bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-semibold ${
                    featuredTrailers[position] ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    Slot {position}
                  </span>
                  {featuredTrailers[position] && (
                    <button
                      onClick={() => onRemoveTrailer(position)}
                      className="text-red-400 hover:text-red-300 text-xs"
                      disabled={savingTrailer}
                      data-testid={`trailer-remove-${position}`}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {featuredTrailers[position] ? (
                  <div className="mb-3">
                    <div className="aspect-[3/4] bg-gray-800 rounded overflow-hidden mb-2">
                      <img
                        src={getCoverThumbUrl(featuredTrailers[position].cover_image_url)}
                        alt={featuredTrailers[position].title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                        }}
                      />
                    </div>
                    <p className="text-white text-sm font-medium truncate">
                      {featuredTrailers[position].title}
                    </p>
                    <p className="text-gray-400 text-xs truncate">
                      {featuredTrailers[position].author}
                    </p>
                  </div>
                ) : (
                  <div className="aspect-[3/4] bg-gray-800/50 rounded flex items-center justify-center mb-3">
                    <div className="text-center">
                      <Play className="h-8 w-8 text-gray-600 mx-auto mb-1" />
                      <p className="text-gray-500 text-xs">Empty Slot</p>
                    </div>
                  </div>
                )}

                <select
                  value={featuredTrailers[position]?.id || ''}
                  onChange={(e) => onSetTrailer(position, e.target.value)}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-red-500 focus:outline-none"
                  disabled={savingTrailer}
                  data-testid={`trailer-select-${position}`}
                >
                  <option value="" className="bg-gray-900">
                    {featuredTrailers[position] ? '-- Change book --' : '-- Select book --'}
                  </option>
                  {booksWithTrailers.map((book) => (
                    <option key={book.id} value={book.id} className="bg-gray-900">
                      {book.title}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <p className="text-gray-500 text-sm mt-4 text-center">
            These trailers will be displayed on the public landing page in the positions shown above.
          </p>
        </>
      )}
    </div>
  );
};

export default FeaturedTrailersPanel;
